<template>
  <div class="commitment">
    <!-- Same card the buy form opens with. What you can spend is the first thing you need
         when deciding how much to commit, and the hold comes straight out of it. -->
    <div class="balance-card">
      <div class="balance-card__icon">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M21 18V19C21 20.1 20.1 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.89 3 5 3H19C20.1 3 21 3.9 21 5V6H12C10.89 6 10 6.9 10 8V16C10 17.1 10.89 18 12 18H21ZM12 16H22V8H12V16ZM16 13.5C15.17 13.5 14.5 12.83 14.5 12C14.5 11.17 15.17 10.5 16 10.5C16.83 10.5 17.5 11.17 17.5 12C17.5 12.83 16.83 13.5 16 13.5Z" fill="currentColor"/>
        </svg>
      </div>
      <div class="balance-card__info">
        <span class="balance-card__label">Available Cash</span>
        <span class="balance-card__value">{{ money(balance) }}</span>
        <!-- Only when something is held. It accounts for why the figure above is lower than
             they may expect, at the moment they are deciding how much to commit. -->
        <span v-if="heldForBids > 0" class="balance-card__held">
          {{ money(heldForBids) }} held for your open bids
        </span>
      </div>
    </div>

    <p class="commitment__lead">{{ amending ? amendLead : lead }}</p>

    <!-- Before the amount, because the minimum depends on which book you are bidding into.
         Only combined offerings have these; everything else is one book. -->
    <template v-if="isCombined">
      <label class="commitment__label">Which part of this offering?</label>
      <p v-if="amending" class="commitment__hint commitment__hint--tight">
        A bid stays in the part it was placed in. To move it, withdraw this bid and place a new
        one.
      </p>
      <div class="tranches">
        <button
          v-for="t in tranches"
          :key="t.key"
          type="button"
          class="tranche"
          :class="{
            'tranche--active': t.key === trancheKey,
            'tranche--locked': amending,
            'tranche--closed': !isOpenToUs(t),
          }"
          :disabled="(amending && t.key !== trancheKey) || !isOpenToUs(t)"
          @click="selectTranche(t.key)"
        >
          <span class="tranche__head">
            <span class="tranche__name">{{ t.name || t.key }}</span>
            <span v-if="audienceLabel(t)" class="tranche__audience">{{ audienceLabel(t) }}</span>
            <!-- Stated rather than just greyed out: a disabled control with no reason reads as
                 a bug. We are an organisation, so an individuals-only tranche is never ours. -->
            <span v-if="!isOpenToUs(t)" class="tranche__closed">Not open to organisations</span>
          </span>
          <span class="tranche__terms">
            <template v-if="t.size">{{ money(t.size) }} on offer</template>
            <template v-if="t.min_commitment">
              <template v-if="t.size"> · </template>from {{ money(t.min_commitment) }}
            </template>
            <template v-if="t.max_commitment">
              up to {{ money(t.max_commitment) }}
            </template>
          </span>
        </button>
      </div>
    </template>

    <!-- Amount -->
    <label class="commitment__label" for="commit-amount">
      How much do you want to commit?
    </label>
    <div class="commitment__field">
      <span class="commitment__prefix">{{ symbol }}</span>
      <input
        id="commit-amount"
        v-model="amount"
        type="text"
        inputmode="decimal"
        class="commitment__input"
        placeholder="0"
        @input="onAmountInput"
      />
    </div>
    <p class="commitment__hint">
      Minimum {{ money(amountBounds.min) }}<template v-if="amountBounds.max">
        · maximum {{ money(amountBounds.max) }}</template>
    </p>

    <!-- Bid level. Absent on a fixed-price offering with a window: the price is already set
         and the book exists only to size the scale-back. -->
    <template v-if="needsLimit">
      <label class="commitment__label" for="commit-bid">{{ bidLabel }}</label>
      <div class="commitment__field">
        <span v-if="!isRateBased" class="commitment__prefix">{{ symbol }}</span>
        <input
          id="commit-bid"
          v-model="bid"
          type="text"
          inputmode="decimal"
          class="commitment__input"
          :disabled="strike"
          :placeholder="guidance ? withCommas(String(guidance.floor)) : '0'"
          @input="onBidInput"
        />
        <span v-if="isRateBased" class="commitment__suffix">%</span>
      </div>
      <p v-if="guidance" class="commitment__hint">
        {{ guidanceHint }}
      </p>

      <label v-if="token.allow_strike_bids" class="commitment__strike">
        <input type="checkbox" v-model="strike" @change="clearError" />
        <span>
          Bid at the final {{ noun }}
          <em>
            Your bid stays eligible whatever the book settles at, so you are not priced out,
            but you accept the {{ noun }} without seeing it first.<template v-if="payYourBid">
              On this offer that also means you settle at the final {{ noun }} rather than at a
              {{ isRateBased ? "rate" : "price" }} of your own.</template>
          </em>
        </span>
      </label>
    </template>

    <!-- What is actually being committed. This is the part a fixed-price form never had to
         say: the units and the final cost are not knowable yet. -->
    <div class="commitment__summary">
      <div class="commitment__row">
        <span>Your bid</span>
        <span>{{ money(numericAmount) }}</span>
      </div>
      <div class="commitment__row">
        <span>Transaction fee ({{ feePercent }}%)</span>
        <span>{{ money(fee) }}</span>
      </div>
      <div class="commitment__row commitment__row--total">
        <span>Held from your wallet</span>
        <span>{{ money(held) }}</span>
      </div>
      <!-- Amending moves an existing hold rather than adding a second one. Without this the
           summary reads as if the full amount is being taken again. -->
      <div v-if="amending && holdDelta !== 0" class="commitment__row">
        <span>{{ holdDelta > 0 ? "Additional hold" : "Released back to you" }}</span>
        <span>{{ money(Math.abs(holdDelta)) }}</span>
      </div>
      <p class="commitment__held-note">
        The fee is charged on top of your bid, so the total amount held in escrow is larger than the amount you
        commit. This money is <strong>held, not taken.</strong> It stays in escrow,
        unavailable to spend, until the allotment is approved.
      </p>
    </div>

    <div class="commitment__outcome">
      <h4>What happens next</h4>
      <ol>
        <li>Bidding closes {{ closeLabel }}.</li>
        <li>
          {{ issuerName }} sets a final {{ noun }} from the demand received{{
            needsLimit ? `, and your bid qualifies if it is at or ${isRateBased ? "below" : "above"} it` : ""
          }}.
        </li>
        <!-- Under pay-your-bid the clearing level only decides WHO is filled; what each
             winner pays is their own bid. Left unsaid, the step above reads as the price. -->
        <li v-if="payYourBid">
          {{ strike ? "Your bid named no level, so it settles at that final " + noun + "." :
             "You settle at your own bid of " + levelLabel + ", not at that final " + noun + "." }}
        </li>
        <li>
          If the offer is oversubscribed you may be allotted less than you asked for. You will
          not be allotted more.
        </li>
        <li>Anything not allotted is released back to your wallet.</li>
      </ol>
    </div>

    <p v-if="error" class="commitment__error">{{ error }}</p>

    <button class="commitment__submit" :disabled="!canSubmit || submitting" @click="review">
      {{ amending ? "Review changes" : "Review bid" }}
    </button>

    <!-- Placing a bid puts a hold on real money, so it goes through the same confirm-then-PIN
         gate as a purchase. Submitting straight from the form asked less of an investor than
         buying does, for an action that is just as binding. -->
    <Teleport to="#teleport-modal">
      <SmallModal v-if="showConfirm" class="tw-z-50" @close="showConfirm = false">
        <template v-slot:content>
          <div class="confirm">
            <h3 class="confirm__title">{{ amending ? "Confirm changes" : "Confirm your bid" }}</h3>

            <div class="confirm__card">
              <div class="confirm__row">
                <span>Bid</span>
                <span>{{ money(numericAmount) }}</span>
              </div>
              <div class="confirm__row">
                <span>{{ strike ? "At" : bidLabel }}</span>
                <span>{{ strike ? `the final ${noun}` : levelLabel }}</span>
              </div>
              <div class="confirm__row">
                <span>Transaction fee ({{ feePercent }}%)</span>
                <span>{{ money(fee) }}</span>
              </div>
              <div class="confirm__divider"></div>
              <div class="confirm__row confirm__row--total">
                <span>{{ amending && holdDelta !== 0 ? "New hold" : "Held from your wallet" }}</span>
                <span>{{ money(held) }}</span>
              </div>
              <div v-if="amending && holdDelta !== 0" class="confirm__row">
                <span>{{ holdDelta > 0 ? "Additional hold" : "Released back to you" }}</span>
                <span>{{ money(Math.abs(holdDelta)) }}</span>
              </div>
            </div>

            <p class="confirm__note">
              This is a bid, not a purchase. Your funds are held until the allotment is
              approved, and you may be allotted less than you asked for.<template v-if="payYourBid && !strike">
                If you are filled you settle at {{ levelLabel }}, not at whatever the offer
                clears at.</template> Bidding closes {{ closeLabel }}.
            </p>

            <p v-if="error" class="commitment__error">{{ error }}</p>

            <PinConfirmation
              class="confirm__pin"
              :btnTitle="amending ? 'Confirm changes' : 'Place bid'"
              @confirmed="submit"
            />
          </div>
        </template>
      </SmallModal>
    </Teleport>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { convertToLocale, getCurrencySymbol } from "@/utils/helpers.js";
import {
  AuctionPricing,
  OfferingTypes,
  formatOfferMoment,
  trancheAudienceLabel,
  TrancheInvestors,
} from "@/utils/offering";
import SmallModal from "@/components/general/SmallModal.vue";
import PinConfirmation from "@/components/general/PinConfirmation.vue";

export default {
  name: "CommitmentForm",

  components: { SmallModal, PinConfirmation },

  emits: ["completed"],

  props: {
    // Set when arriving from My Bids to change an existing bid. Absent for a new one.
    amendId: { type: String, default: "" },
  },

  data() {
    return {
      amount: "",
      bid: "",
      trancheKey: "",
      strike: false,
      error: "",
      submitting: false,
      existing: null,
      showConfirm: false,
    };
  },

  async created() {
    if (!this.amendId) return;
    // There is no single-commitment endpoint, so the list is the source. Prefilling matters:
    // an amend form that opens empty invites someone to retype a bid they meant to nudge.
    await this.$store.dispatch("TokenModule/getCommitments", { limit: 100 });
    const found = (this.$store.state.TokenModule.commitments?.docs || []).find(
      (c) => String(c._id) === String(this.amendId)
    );
    if (!found) return;
    this.existing = found;
    // An amendment cannot move between tranches: they are separate books with their own terms,
    // so changing one would be a withdrawal and a new bid rather than an edit. The API derives
    // it from the commitment; this is only so the form shows which book it belongs to.
    this.trancheKey = found.tranche || "";
    // Prefilled through the same formatter, so an amended bid does not open unseparated.
    this.amount = this.withCommas(String(found.amount));
    this.strike = Boolean(found.is_strike_bid);
    this.bid = found.bid_price
      ? String(found.bid_price)
      : found.bid_rate
      ? String(found.bid_rate)
      : "";
  },

  computed: {
    ...mapState({
      token: (state) => state.TokenModule.tokenInfo,
    }),
    // Through the getter, not raw state. State holds `{ gNGN: 12345, gUSD: 500 }` keyed by
    // gToken; the getter turns it into [{ currency, balance }]. Reading state directly and
    // calling .find() on it threw during render, which is why the form came up blank.
    ...mapGetters("organisationModule", ["walletBalance", "currencyWallets"]),

    /**
     * Decides whether the bid is sent as bid_rate or bid_price, so it has to agree with the
     * API exactly. Its isRateBased is [DEBT, FixedInterest]; Fund was in this list, which meant
     * a fund bid was collected as a percentage and submitted in the wrong field entirely.
     */
    isRateBased() {
      return ["Debt", "Fixed Interest"].includes(this.token?.investment_type);
    },
    noun() {
      return this.isRateBased ? "rate" : "price";
    },
    /**
     * Combined offerings only. Every other type is a single book, and the API rejects a bid
     * that names a tranche on one, so this stays false and nothing below renders.
     */
    isCombined() {
      return (
        this.token?.offering_type === "COMBINED" &&
        Array.isArray(this.token?.tranches) &&
        this.token.tranches.length > 0
      );
    },
    tranches() {
      return this.isCombined ? this.token.tranches : [];
    },
    tranche() {
      if (!this.isCombined || !this.trancheKey) return null;
      return this.tranches.find((t) => t.key === this.trancheKey) || null;
    },

    /**
     * Mirrors boundsFor on the server: a tranche's own guidance and reserve where it sets them,
     * the offering's where it does not. Reading the offering's range while a tranche overrides
     * it would show a bid as in range and have the API refuse it.
     */
    guidance() {
      const g = this.tranche?.price_guidance?.floor !== undefined
        ? this.tranche.price_guidance
        : this.token?.price_guidance;
      return g && g.floor !== undefined && g.cap !== undefined ? g : null;
    },

    /**
     * What this book takes, which is not what the offering takes. A tranche's minimum is
     * checked separately from min_trade, so both have to hold.
     */
    amountBounds() {
      const min = Number(this.tranche?.min_commitment) > 0
        ? Number(this.tranche.min_commitment)
        : Number(this.token?.min_trade?.buy || 0);
      const max = Number(this.tranche?.max_commitment) > 0
        ? Number(this.tranche.max_commitment)
        : Number(this.token?.max_trade?.buy || 0);
      return { min, max };
    },
    // A fixed-price offering that happens to have a window takes no limit — there is nothing
    // to bid within, since the price is already set.
    needsLimit() {
      return this.token?.offering_type !== "FIXED_PRICE";
    },
    bidLabel() {
      return this.isRateBased
        ? "The highest rate you require"
        : "The highest price you will pay";
    },
    levelLabel() {
      if (!(this.numericBid > 0)) return "—";
      return this.isRateBased ? `${this.bid}%` : this.money(this.numericBid);
    },
    guidanceHint() {
      const { floor, cap, step } = this.guidance;
      const unit = this.isRateBased ? "%" : "";
      // Same separators as the field itself — a hint that reads "9500" against an input
      // showing "9,500" makes the sponsor's range look like a different number.
      const n = (v) => (this.isRateBased ? v : this.withCommas(String(v)));
      const range = `Bids run from ${n(floor)}${unit} to ${n(cap)}${unit}`;
      return step ? `${range}, in steps of ${n(step)}${unit}.` : `${range}.`;
    },

    currency() {
      return this.token?.currency || "USD";
    },
    symbol() {
      return getCurrencySymbol(this.currency);
    },
    /**
     * walletBalance is an array of per-currency wallets, not a number.
     *
     * The guard below previously compared the required amount against the array itself:
     * Number([...]) is NaN for anything but a single-element array, and every comparison
     * against NaN is false, so the insufficient-funds check never fired.
     */
    balance() {
      const wallet = (this.walletBalance || []).find((w) => w.currency === this.currency);
      return Number(wallet?.balance || 0);
    },
    // From /token/currency-wallets, which carries the buckets the balance endpoint has no
    // room for. Zero until that call lands, so the line simply does not render.
    heldForBids() {
      const wallet = (this.currencyWallets || []).find((w) => w.currency === this.currency);
      return Number(wallet?.offer_hold || 0);
    },
    // Who is actually setting the price. "The sponsor" is the market's word for it, not the
    // investor's — they know the name on the deal they are bidding into.
    issuerName() {
      return this.token?.creator?.name || "The issuer";
    },
    numericAmount() {
      const n = Number(String(this.amount).replace(/,/g, ""));
      return Number.isFinite(n) ? n : 0;
    },
    numericBid() {
      const n = Number(String(this.bid).replace(/,/g, ""));
      return Number.isFinite(n) ? n : null;
    },
    feePercent() {
      return Number(this.token?.buy_fee || 0);
    },
    fee() {
      return (this.numericAmount * this.feePercent) / 100;
    },
    // Mirrors the server: hold = amount + fee. Showing the bare bid here and holding more
    // would make the wallet look wrong immediately after submitting.
    held() {
      return this.numericAmount + this.fee;
    },

    closeLabel() {
      return formatOfferMoment(this.token?.offer_closes_at, this.token?.market_timezone);
    },
    amendLead() {
      return "Changing your bid replaces the one you placed. The previous version is kept on record, and your hold moves by the difference.";
    },
    // Whether each winner settles at their own bid rather than at the clearing level. Auctions
    // only, and the backend defaults the field to UNIFORM — so an unset auction is not one.
    payYourBid() {
      return (
        this.token?.offering_type === OfferingTypes.AUCTION
        && this.token?.auction_pricing === AuctionPricing.MULTIPLE
      );
    },

    lead() {
      if (this.token?.offering_type === OfferingTypes.AUCTION) {
        return this.payYourBid
          ? "Bids are ranked from best to worst and filled in that order until the offer is exhausted. Each winning bid settles at its own level."
          : "Bids are ranked from best to worst and filled in that order until the offer is exhausted. Every winning bid settles at the same final level.";
      }
      return "You are placing a bid, not buying. The final price and how much you receive are decided after the book closes.";
    },

    amending() {
      return Boolean(this.existing);
    },
    // Amending adjusts the existing hold by the difference rather than placing a second one.
    // Saying which way it moves answers "I already have money held, why is it asking again".
    holdDelta() {
      if (!this.amending) return 0;
      return this.held - Number(this.existing.hold_amount || 0);
    },

    canSubmit() {
      if (!(this.numericAmount > 0)) return false;
      if (this.needsLimit && !this.strike && !(this.numericBid > 0)) return false;
      return true;
    },
  },

  methods: {
    money(value) {
      return `${this.symbol}${value ? convertToLocale(Number(value) || 0) : "0"}`;
    },
    clearError() {
      this.error = "";
    },

    /**
     * Thousand separators as you type. Amounts here run to seven figures — an unseparated
     * "4200000" is genuinely hard to read back, and misreading it is a real bid at the wrong
     * size.
     *
     * A trailing "." is preserved so "1234." can become "1234.5" rather than being eaten
     * mid-keystroke, and decimals are capped at two.
     */
    withCommas(raw) {
      const cleaned = String(raw ?? "").replace(/[^\d.]/g, "");
      if (!cleaned) return "";

      const [whole, ...rest] = cleaned.split(".");
      const hasDot = cleaned.includes(".");
      const decimals = rest.join("").slice(0, 2);
      const grouped = whole ? Number(whole).toLocaleString("en-US") : "";

      if (!hasDot) return grouped;
      return `${grouped || "0"}.${decimals}`;
    },

    onAmountInput() {
      this.amount = this.withCommas(this.amount);
      this.clearError();
    },

    onBidInput() {
      // Rates are small numbers where separators add nothing — 12.5, not 12.5 grouped.
      if (!this.isRateBased) this.bid = this.withCommas(this.bid);
      this.clearError();
    },

    /**
     * Client-side mirror of assertBidWithinBounds. The server is the authority — this exists
     * so an investor is told before submitting rather than after, and the wording matches what
     * the field asked for.
     */
    localBidError() {
      if (!this.needsLimit || this.strike || !this.guidance) return "";
      const { floor, cap, step } = this.guidance;
      const value = this.numericBid;
      const unit = this.isRateBased ? "%" : "";

      if (value < floor) return `Bids start at ${floor}${unit}.`;
      if (value > cap) return `Bids go up to ${cap}${unit}.`;
      if (step) {
        // Same rounding guard as the server: (9.3 - 9) % 0.1 is not 0 in floating point.
        const ticks = (value - floor) / step;
        if (Math.abs(ticks - Math.round(ticks)) > 1e-9) {
          return `Bids move in steps of ${step}${unit}.`;
        }
      }
      return "";
    },

    audienceLabel(tranche) {
      return trancheAudienceLabel(tranche);
    },

    /**
     * Whether this tranche takes bids from us.
     *
     * This app authenticates organisations only (POST /company/login, org_token), so the
     * principal's entity_type is always "organisation" — the same value assertEligibleForTranche
     * compares against server-side. investor-app cannot know this about its user and has to let
     * the API reject; here the tranche can simply be shown as closed before anyone bids into it.
     */
    isOpenToUs(tranche) {
      const investors = tranche?.investors || TrancheInvestors.ANY;
      return investors === TrancheInvestors.ANY
        || investors === TrancheInvestors.ORGANISATION;
    },

    selectTranche(key) {
      // Belt and braces: the button is disabled, but a default selection could still land
      // on a tranche that is not ours if the offering changes underneath.
      const target = this.tranches.find((t) => t.key === key);
      if (target && !this.isOpenToUs(target)) return;
      if (this.amending) return;
      this.trancheKey = key;
      this.clearError();
    },

    /**
     * Client-side mirror of assertEligibleForTranche's amount checks. The audience check is
     * deliberately not mirrored: this app cannot tell an organisation account from an
     * individual one, and a guess that refuses an eligible investor is worse than the API's
     * refusal, which at least names the tranche and is correct.
     */
    localAmountError() {
      const { min, max } = this.amountBounds;
      const value = this.numericAmount;
      const where = this.tranche ? `${this.tranche.name || this.tranche.key} takes` : "This offering takes";
      if (min > 0 && value < min) return `${where} commitments from ${this.money(min)}.`;
      if (max > 0 && value > max) return `${where} commitments up to ${this.money(max)}.`;
      return "";
    },

    /**
     * Everything that can be checked without spending anything happens here, before the
     * confirm screen. Failing after someone has entered their PIN would be the wrong order.
     */
    review() {
      if (this.isCombined && !this.trancheKey) {
        this.error = "Choose which part of this offering you are bidding into.";
        return;
      }
      const amountError = this.localAmountError();
      if (amountError) {
        this.error = amountError;
        return;
      }
      const bidError = this.localBidError();
      if (bidError) {
        this.error = bidError;
        return;
      }
      // On an amend only the increase needs covering — the rest is already held.
      const needed = this.amending ? this.holdDelta : this.held;
      if (needed > this.balance) {
        this.error = `You need ${this.money(needed)} available. Fund your wallet and try again.`;
        return;
      }
      this.error = "";
      this.showConfirm = true;
    },

    async submit() {
      this.submitting = true;
      const values = {
        amount: this.numericAmount,
        isStrikeBid: this.strike,
        ...(this.isRateBased ? { bidRate: this.numericBid } : { bidPrice: this.numericBid }),
      };
      const response = this.amending
        ? await this.$store.dispatch("TokenModule/amendCommitment", {
            commitmentId: this.existing._id,
            ...values,
          })
        : await this.$store.dispatch("TokenModule/submitCommitment", {
            id: this.token._id,
            // Only on a combined offering. The API refuses a tranche on a single-book offering
            // rather than ignoring it, so an empty string must not be sent.
            ...(this.isCombined ? { tranche: this.trancheKey } : {}),
            ...values,
          });
      this.submitting = false;

      if (response) {
        this.showConfirm = false;
        // Placing a bid holds real money, so it needs the same confirmation any other
        // money-moving action gets. Prefers the API's own wording over a generic string.
        this.$store.dispatch("showToast", {
          description:
            response.data?.message ||
            (this.amending
              ? "Your bid has been updated."
              : "Your bid has been placed and the funds are held until allotment."),
          display: true,
          type: "success",
        });
        this.$emit("completed", response.data?.data);
        return;
      }

      // The store action catches and returns false, so without this a failure leaves the
      // confirm modal sitting there having apparently done nothing — which is exactly how a
      // bare ReferenceError in the API layer presented.
      this.error =
        "We couldn't place that bid. Nothing has been held, so please try again, or contact support if it keeps happening.";
    },
  },
};
</script>

<style lang="scss" scoped>
$text-primary: #0f172a;
$text-secondary: #475569;
$text-muted: #64748b;
$border-color: #e2e8f0;
$bg-subtle: #f8fafc;
$accent: #4d84ff;
$error: #dc2626;

.balance-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(0, 18, 64, 0.03) 0%, rgba(77, 132, 255, 0.05) 100%);
  border: 1px solid $border-color;
  border-radius: 10px;
  margin-bottom: 24px;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 8px;
    background: #fff;
    color: $accent;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__label {
    font-size: 12px;
    color: $text-muted;
  }

  &__value {
    font-size: 18px;
    font-weight: 600;
    color: $text-primary;
    font-variant-numeric: tabular-nums;
  }

  &__held {
    font-size: 12px;
    color: $text-secondary;
  }
}

.tranches {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.tranche {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 12px 14px;
  text-align: left;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 150ms ease, background-color 150ms ease;

  &:hover:not(:disabled) {
    border-color: #cbd5e1;
  }

  &--active {
    border-color: #4d84ff;
    background: #f5f8ff;
  }

  &--locked {
    cursor: default;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
  }

  &__name {
    font-size: 14px;
    font-weight: 500;
    color: #0f172a;
  }

  &__audience {
    font-size: 11px;
    color: #475569;
    white-space: nowrap;
  }

  &__terms {
    font-size: 12px;
    color: #64748b;
  }
}

.commitment {
  display: flex;
  flex-direction: column;

  &__lead {
    font-size: 15px;
    line-height: 1.6;
    color: $text-secondary;
    margin: 0 0 20px;
  }

  &__label {
    font-size: 14px;
    font-weight: 500;
    color: $text-primary;
    margin-bottom: 8px;
  }

  // The field owns its bottom spacing rather than relying on the hint beneath it — an auction
  // has no guidance to hint, so that field would otherwise sit flush against the next label.
  &__field {
    display: flex;
    align-items: center;
    gap: 6px;
    border: 1px solid $border-color;
    border-radius: 8px;
    padding: 0 12px;
    margin-bottom: 20px;
    background: #fff;

    &:focus-within {
      border-color: $accent;
    }
  }

  &__prefix,
  &__suffix {
    font-size: 14px;
    color: $text-muted;
  }

  &__input {
    flex: 1;
    border: 0;
    outline: none;
    padding: 12px 0;
    font-size: 15px;
    color: $text-primary;
    background: transparent;
    font-variant-numeric: tabular-nums;

    &:disabled {
      color: $text-muted;
    }
  }

  // Pulled up under its field, since the field now carries the gap for both of them.
  &__hint {
    font-size: 12px;
    line-height: 1.5;
    color: $text-muted;
    margin: -14px 0 20px;
  }

  // After &__hint, not before: same specificity, so source order decides. It also replaces the
  // negative top margin outright, which is tuned for a hint sitting under an input rather than
  // under a label, where it would ride up over the label text.
  &__hint--tight {
    margin: 0 0 12px;
  }

  &__strike {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-bottom: 20px;
    font-size: 13px;
    color: $text-primary;
    cursor: pointer;

    em {
      display: block;
      font-style: normal;
      font-size: 12px;
      line-height: 1.5;
      color: $text-muted;
      margin-top: 2px;
    }
  }

  &__summary {
    background: $bg-subtle;
    border: 1px solid $border-color;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 18px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    color: $text-secondary;
    padding: 4px 0;
    font-variant-numeric: tabular-nums;

    &--total {
      border-top: 1px solid $border-color;
      margin-top: 6px;
      padding-top: 10px;
      font-weight: 600;
      color: $text-primary;
    }
  }

  &__held-note {
    font-size: 12px;
    line-height: 1.6;
    color: $text-secondary;
    margin: 10px 0 0;
  }

  &__outcome {
    margin-bottom: 18px;

    h4 {
      font-size: 13px;
      font-weight: 600;
      color: $text-primary;
      margin: 0 0 8px;
    }

    ol {
      margin: 0;
      padding-left: 18px;
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 12px;
      line-height: 1.6;
      color: $text-secondary;
    }
  }

  &__error {
    font-size: 13px;
    color: $error;
    margin: 0 0 12px;
  }

  &__submit {
    padding: 13px 20px;
    border: 0;
    border-radius: 8px;
    background: $accent;
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;

    &:disabled {
      background: #cbd5e1;
      cursor: not-allowed;
    }
  }
}

// Teleport moves the confirm markup to #teleport-modal, outside .commitment's subtree, so
// these cannot be nested under it — a descendant selector would never match.
.confirm {
  text-align: left;

  &__title {
    font-size: 17px;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 16px;
  }

  &__card {
    background: $bg-subtle;
    border: 1px solid $border-color;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 14px;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 0;
    font-size: 13px;
    color: $text-secondary;
    font-variant-numeric: tabular-nums;

    &--total {
      font-weight: 600;
      color: $text-primary;
    }
  }

  &__divider {
    height: 1px;
    background: $border-color;
    margin: 8px 0;
  }

  &__note {
    font-size: 12px;
    line-height: 1.6;
    color: $text-secondary;
    margin: 0 0 16px;
  }

  &__pin {
    margin-top: 4px;
  }
}

.tranche--closed {
  opacity: 0.55;
  cursor: not-allowed;
}

.tranche__closed {
  display: block;
  font-size: 11px;
  color: #b45309;
}
</style>
